import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Table } from 'antd';

import DefaultButton from 'components/button/DefaultButton';

import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import { useAtomValue } from 'jotai';
import CardInputModal from 'components/modal/CardInputModal';
import { CardType } from 'components/modal/SubscribeModal';
import userInforAtom from 'atom/userInfoAtom';
import { useQuery } from '@tanstack/react-query';
import { QueryKeys, restfetcher } from 'queryClient';
import { CardInforcolumns } from 'consts/table';

interface MembershipCardProps {
  setRegisteredCard: (card: { cardname: string; cardnumber: string }) => void;
}

const MembershipCard = ({ setRegisteredCard }: MembershipCardProps) => {
  const [selectedCard, setSelectedCard] = useState<CardType>('individual');

  const [openCardInputModal, setOpenCardInputModal] = useState(false);

  const userInfor = useAtomValue(userInforAtom);

  const handleOpenModal = () => {
    setOpenCardInputModal(true);
  };

  const handleCloseModal = () => {
    setOpenCardInputModal(false);
  };

  const requestBillingInfor = useQuery({
    queryKey: [QueryKeys.MEMBERSHIP_BILLING],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/my/membership/billing',
        params: {
          usridx: userInfor.usridx // 249
        }
      })
  });

  const billingList: CardBillingInfo[] = requestBillingInfor?.data?.billing || [];

  const billingData = billingList?.map((item: CardBillingInfo) => {
    return {
      key: item.cardnumber,
      card: item.cardname,
      cardNumber: item.cardnumber,
      manage: (
        <ButtonWrapper>
          <DefaultButton
            onClick={handleOpenModal}
            text="결제수단변경"
            size="small"
            type="primary"
          />
        </ButtonWrapper>
      )
    };
  });

  useEffect(() => {
    if (userInfor.usridx !== 0) {
      requestBillingInfor.refetch();
    }
  }, [userInfor.usridx]);

  useEffect(() => {
    if (billingList.length > 0) {
      setRegisteredCard({
        cardname: billingList[0]?.cardname,
        cardnumber: billingList[0]?.cardnumber
      });
    }
  }, [billingList]);

  return (
    <>
      <h2>결제 수단 관리</h2>
      <BizTitleWrapper>
        <PaymentDetails>
          <Table dataSource={billingData} bordered columns={CardInforcolumns} pagination={false} />
        </PaymentDetails>
        <TextWrapper>
          <ul>
            <li>
              결제수단을 변경하시더라도 결제가 중단되거나 결제일이 변경되지 않으니 안심하고
              변경하세요.
            </li>
            <li>변경된 결제 수단은 다음 결제일에 자동으로 반영됩니다.</li>
          </ul>
        </TextWrapper>
        <InfoTextWrapper>
          <h3>멤버십 관리 유의사항</h3>
          <ul>
            <li>
              결제카드변경 : 자동결제중인 결제 건의 카드 변경을 원하실 경우 1)멤버십 구독을 해지하여
              주시고 2)해지된 날짜 이후에 새로 멤버십을 구독하여 주세요.
            </li>
            <li>
              환불 : 결제상세 페이지 내에서 요청 할 수 있습니다. 환불 요청 후 관리자 승인과정을 거처
              환불이 진행됩니다.
            </li>
            <li>멤버십티켓의 환불은 &lt;서비스이용표준약관&gt;의 기준에 맞춰 가능합니다.</li>
          </ul>
        </InfoTextWrapper>
      </BizTitleWrapper>
      {openCardInputModal && (
        <CardInputModal
          selectedCard={selectedCard}
          setSelectedCard={setSelectedCard}
          open={openCardInputModal}
          isChangeCard={true}
          onCancel={handleCloseModal}
        />
      )}
    </>
  );
};

export default MembershipCard;

const TextWrapper = styled.div`
  padding: 24px 0 48px;
  color: ${color.gray500};
  ${font.caption1_regular};

  li {
    color: ${color.gray500};
    ${font.caption1_regular};

    &::before {
      content: '•';
      margin-inline-end: 1ch;
      color: ${color.gray500};
    }
  }
`;

const InfoTextWrapper = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  gap: 16px;
  border-radius: 8px;
  border: 1px solid ${color.opacityBlack01};

  h3 {
    ${font.body_medium};
  }

  li {
    color: ${color.gray700};
    ${font.caption1_regular};

    &::before {
      content: '•';
      margin-inline-end: 1ch;
      color: ${color.gray700};
    }
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const PaymentDetails = styled.div`
  tbody tr {
    text-align: center;

    td {
      vertical-align: middle;
    }
  }

  p.date {
    color: ${color.gray600};
    ${font.caption3_regular};
  }
`;

const BizTitleWrapper = styled.div`
  margin-top: 24px;
  margin-bottom: 10px;
`;